import { useForm } from 'react-hook-form'
import { ClinicGroup } from '@magnetar/admin-api-interfaces'
import { Button } from '@/components/ui/button'
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Checkbox } from '@/components/ui/checkbox'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { clinicGroupService } from '@/services/clinic_group_service'
import { toast } from 'sonner'
import { useAuth } from '@/hooks/useAuth'
import { useEffect } from 'react'
import { Skeleton } from '@/components/ui/skeleton'

type ClinicGroupFormValues = {
  group_name: string
  group_code?: string
  manager_name: string
  phone: string
  email?: string
  address?: string
  description?: string
  is_active: boolean
}

interface ClinicGroupInfoProps {
  clinicGroup?: ClinicGroup
  isLoading?: boolean
}

export default function ClinicGroupInfo({ clinicGroup, isLoading }: ClinicGroupInfoProps) {
  const { token } = useAuth()
  const form = useForm<ClinicGroupFormValues>({
    defaultValues: {
      group_name: '',
      group_code: '',
      manager_name: '',
      phone: '',
      email: '',
      address: '',
      description: '',
      is_active: true,
    },
  })

  // Update form values when clinicGroup data changes
  useEffect(() => {
    if (clinicGroup) {
      form.reset({
        group_name: clinicGroup.group_name,
        group_code: clinicGroup.group_code || '',
        manager_name: clinicGroup.manager_name,
        phone: clinicGroup.phone,
        email: clinicGroup.email || '',
        address: clinicGroup.address || '',
        description: clinicGroup.description || '',
        is_active: clinicGroup.is_active,
      })
    }
  }, [clinicGroup, form])

  async function onSubmit(data: ClinicGroupFormValues) {
    if (!clinicGroup || !token) return

    try {
      const updateData = {
        group_name: data.group_name,
        group_code: data.group_code || null,
        manager_name: data.manager_name,
        phone: data.phone,
        email: data.email || null,
        address: data.address || null,
        description: data.description || null,
        is_active: data.is_active,
      }
      const response = await clinicGroupService.updateClinicGroup(
        clinicGroup.clinic_group_id,
        updateData,
        token,
      )
      if (response.success) {
        toast.success('診所集團更新成功')
      } else {
        toast.error('診所集團更新失敗')
      }
    } catch {
      toast.error('操作失敗，請稍後再試')
    }
  }

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <Skeleton className="h-6 w-[150px]" />
          <Skeleton className="h-4 w-[200px]" />
        </CardHeader>
        <CardContent className="grid gap-6">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
        </CardContent>
      </Card>
    )
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
        <Card>
          <CardHeader>
            <CardTitle>基本資料</CardTitle>
            <CardDescription>診所集團的基本資訊</CardDescription>
          </CardHeader>
          <CardContent className="grid gap-6">
            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="group_name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>集團名稱 *</FormLabel>
                    <FormControl>
                      <Input placeholder="請輸入集團名稱" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="group_code"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>集團代碼</FormLabel>
                    <FormControl>
                      <Input placeholder="請輸入集團代碼" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="manager_name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>負責人 *</FormLabel>
                    <FormControl>
                      <Input placeholder="請輸入負責人姓名" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="phone"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>電話 *</FormLabel>
                    <FormControl>
                      <Input placeholder="請輸入電話" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>電子郵件</FormLabel>
                    <FormControl>
                      <Input placeholder="請輸入電子郵件" type="email" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="address"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>地址</FormLabel>
                    <FormControl>
                      <Input placeholder="請輸入地址" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>描述</FormLabel>
                  <FormControl>
                    <Textarea placeholder="請輸入集團描述" className="min-h-[80px]" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="is_active"
              render={({ field }) => (
                <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-4">
                  <FormControl>
                    <Checkbox checked={field.value} onCheckedChange={field.onChange} />
                  </FormControl>
                  <div className="space-y-1 leading-none">
                    <FormLabel>啟用狀態</FormLabel>
                    <FormDescription>設定診所集團是否啟用</FormDescription>
                  </div>
                </FormItem>
              )}
            />
          </CardContent>
        </Card>

        <Button type="submit">更新集團資料</Button>
      </form>
    </Form>
  )
}
